import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BUCKET_URL_KEY,
  REGEX_URL,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import { Images } from "@assets/images";
import { Formik, Form } from "formik";
import { FormControl } from "@app/components/form-control";
import Input from "@app/components/input";
import Textarea from "@app/components/textarea";
import { updateFeedValidationSchema } from "@app/validations";
import UploadMediaService from "@app/services/http/upload-media.service";
import SocialPostsService from "@app/services/http/social-posts.service";
import StorageService from "@core/services/storage";
import { addToast } from "@app/components/toast/toast.service";
import { SocialPostMedia, SocialPostUpdate } from "@app/types";

function UpdateFeedPage() {
  const { feedId } = useParams();
  const navigate = useNavigate();

  const [content, setContent] = useState<string>("");
  const [status, setStatus] = useState<number>(2);
  const [zindex, setZindex] = useState<number | null>(null);
  const [medias, setMedias] = useState<SocialPostMedia[]>([]);

  const s3Url = useMemo(() => StorageService.getObject(BUCKET_URL_KEY), []);

  useEffect(() => {
    if (!feedId) return;

    SocialPostsService.getDetail(+feedId, { isGetComments: false })
      .then((res) => {
        setContent(res.data.content || "");
        setStatus(res.data.status);
        setZindex(res.data.zindex);
        setMedias(res.data.medias || []);
      })
      .catch(() =>
        addToast({ text: SystemMessage.UNKNOWN_ERROR, status: "inValid" })
      );
  }, [feedId]);

  const buildSrc = (url?: string) => {
    if (!url) return "";

    return url.match(REGEX_URL) ? url : s3Url + url;
  };

  const handleUploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    try {
      // Chỉ upload 1 ảnh mỗi lần
      const file = files[0];
      const res = await UploadMediaService.upload({ medias: [file] });
      const uploaded = res.data.items.map((it) => ({
        url: it.url,
        type: it.type,
        ratio: it.ratio,
        width: it.width,
        height: it.height,
        thumbnail: it.thumbnail,
      })) as SocialPostMedia[];

      setMedias((prev) => [...prev, ...uploaded]);
      addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
    } catch (e) {
      addToast({ text: SystemMessage.UNKNOWN_ERROR, status: "inValid" });
    }
  };

  const handleRemoveMedia = (idx: number) => {
    setMedias((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="py-6 px-12">
      <div className="text-[#343C6A]">Danh sách feed / Update</div>

      <div className="mt-6 grid grid-cols-12 gap-6">
        <Formik
          enableReinitialize
          initialValues={{
            content: content,
            zindex: zindex,
            status: status,
          }}
          validationSchema={updateFeedValidationSchema}
          onSubmit={async (values) => {
            if (!feedId) return;

            const payload: SocialPostUpdate = {
              content: values.content,
              medias,
              status: values.status,
              zindex: values.zindex,
            };

            await SocialPostsService.update(+feedId, payload);
            addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
            navigate(`/${RoutePath.FEED_MANAGEMENT}`);
          }}
        >
          {(formik) => (
            <Form className="col-span-8 bg-white p-5 rounded-xl">
              <FormControl name="content">
                <Textarea
                  className="w-full"
                  width="100%"
                  height={500}
                  placeholder="Nhập nội dung bài viết..."
                  textAreaClassName="bg-[rgba(239,241,249,0.6)] rounded-xl p-4 text-[14px] leading-6 placeholder:text-[#A0A7B3] resize-y focus:border-[#5570F1] focus:shadow-[0_0_0_3px_rgba(85,112,241,0.15)]"
                />
              </FormControl>

              <div className="mt-4">
                <div className="text-sm mb-2">ZIndex</div>
                <FormControl name="zindex">
                  <Input
                    type="number"
                    placeholder="Nhập ZIndex"
                    className="w-full"
                    inputClassName="bg-[rgba(239,241,249,0.6)] rounded-xl h-[48px] focus:border-[#5570F1] focus:shadow-[0_0_0_3px_rgba(85,112,241,0.15)]"
                  />
                </FormControl>
              </div>

              <div className="mt-4">
                <div className="text-sm mb-2">Status</div>
                <select
                  className="w-[240px] bg-[rgba(239,241,249,0.6)] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#5570F1] focus:shadow-[0_0_0_3px_rgba(85,112,241,0.15)]"
                  value={formik.values.status}
                  onChange={(e) =>
                    formik.setFieldValue("status", Number(e.target.value))
                  }
                  onBlur={() => formik.setFieldTouched("status", true)}
                >
                  <option value={1}>Approve</option>
                  <option value={2}>Pending</option>
                  <option value={3}>Reject</option>
                </select>
                {formik.touched.status && formik.errors.status && (
                  <div className="flex error-color text-xs">
                    {String(formik.errors.status)}
                  </div>
                )}
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
                >
                  Lưu
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <div className="col-span-4">
          <div className="bg-white p-5 rounded-xl">
            <label className="bg-[#F5F6FC] rounded-xl px-4 py-6 h-[120px] cursor-pointer flex flex-col items-center justify-center gap-2 border border-[#EEF0F6] hover:border-[#5570F1] transition">
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleUploadFiles(e.target.files)}
              />
              <img
                src={Images.UploadCloudIcon.default}
                alt="upload"
                className="w-8 h-8"
              />
              <span className="text-[#5A607F] text-sm">Upload File</span>
            </label>
          </div>

          <div className="mt-4 flex flex-col gap-4">
            {medias
              .filter((m) => m.type === "image")
              .map((m, idx) => (
                <div
                  key={idx}
                  className="relative bg-white rounded-xl overflow-hidden"
                >
                  <img
                    src={buildSrc(m.url)}
                    alt="media"
                    className="w-full object-cover"
                  />
                  <button
                    className="absolute top-2 right-2 rounded-full bg-white/90 hover:bg-white p-1 shadow"
                    onClick={() => handleRemoveMedia(idx)}
                    aria-label="remove"
                  >
                    <img
                      src={Images.CloseCircleIcon.default}
                      alt="remove"
                      className="w-5 h-5"
                    />
                  </button>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdateFeedPage;
